import type { DataGridSettings, JsonDataItem } from '@myosh/odin-components';
import { DataSearchLocation, isDropDownNoSelection, OdinIcon } from '@myosh/odin-components';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ExtendedDynamicFormWorkflowStep } from '../../../../../@types/forms';
import { defaultGridSettings } from '../../../../dynamic-page/default-grid-settings';
import AllRecordsGrid from '../all-records-grid';
import LinkedRecordsGrid from '../linked-records-grid';
import type { LinkedRecordsSelectingProps } from './selection-utility';
import { getColumnsConfigurator, SelectingGridType } from './selection-utility';

const allRecordsColumnsConfigurator = getColumnsConfigurator(SelectingGridType.ALL_RECORDS_GRID);
const selectedRecordsColumnConfigurator = getColumnsConfigurator(SelectingGridType.SELECTED_RECORDS_GRID);

// default grid settings
const buildDefaultGridSettings = (linkedRecordsGrid: SelectingGridType) => {
  const isAllRecordsGrid = linkedRecordsGrid === SelectingGridType.ALL_RECORDS_GRID;
  return {
    ...defaultGridSettings,
    filterLocation: isAllRecordsGrid ? DataSearchLocation.Api : DataSearchLocation.Local,
  } as DataGridSettings;
};

const allRecordsDefaultGridSettings = buildDefaultGridSettings(SelectingGridType.ALL_RECORDS_GRID);
const selectedRecordsDefaultGridSettings = buildDefaultGridSettings(SelectingGridType.SELECTED_RECORDS_GRID);

export default function LinkedRecordsSelectingGrid({
  recordId,
  sourceModuleFormId,
  targetModuleFormId,
  recordLinkType,
  value,
  linkedWorkflowSteps,
  updatePreviewRecord,
  selectedLastEditedFilter,
  selectedConfig,
  selectedRecordsFilter,
  setSelectedRecords,
  previewRecordId,
}: LinkedRecordsSelectingProps) {
  const [linkedRecordIds, setLinkedRecordIds] = useState<Array<string>>(value || []);

  const isCloneRecordLinkType = recordLinkType === 'CLONE';
  const formId = isCloneRecordLinkType ? sourceModuleFormId : targetModuleFormId;

  useEffect(() => {
    if (value && Array.isArray(value)) {
      setLinkedRecordIds(value);
    }
  }, [value]);

  const lastViewedRecord = useRef<number | undefined>(undefined);

  const getColorElementIndicator = useCallback(
    (data?: JsonDataItem) => {
      const backgroundColor = linkedWorkflowSteps?.find(
        (step: ExtendedDynamicFormWorkflowStep) => step.label === data?.status
      )?.backgroundColor;
      return <div className="h-4 w-4 self-center rounded" style={{ backgroundColor: backgroundColor }} />;
    },
    [linkedWorkflowSteps]
  );

  const optionRowIndicator = useMemo(() => {
    return {
      indicator: (data?: JsonDataItem) => {
        // preview icon should be shown on hover or when active
        const previewIconStyles = clsx('text-primary-2 transition-opacity group-hover:odin-opacity-100', {
          'opacity-0': lastViewedRecord.current !== data?.id,
        });
        return (
          <div className="flex flex-row">
            <div
              className="contents"
              onClick={() => {
                const recordId = (data?.id as number) ?? -1;
                lastViewedRecord.current = recordId;
                updatePreviewRecord?.(recordId);
              }}
            >
              <OdinIcon icon="Eye" className={previewIconStyles} />
              {getColorElementIndicator(data)}
            </div>
          </div>
        );
      },
    };
  }, [getColorElementIndicator]);

  const selectedRowIndicator = useMemo(() => {
    return {
      indicator: (data?: JsonDataItem) => {
        return getColorElementIndicator(data);
      },
    };
  }, [getColorElementIndicator]);

  const addLinkedRecord = (id: string) => {
    const index = linkedRecordIds.findIndex((_id) => _id === id);

    if (isCloneRecordLinkType) {
      //For the clone record link type this function works like a toggle add/remove
      if (index === -1) {
        setLinkedRecordIds([...linkedRecordIds, id]);
      } else {
        setLinkedRecordIds(linkedRecordIds.filter((recordId) => recordId !== id));
      }
    } else {
      if (index === -1) {
        setLinkedRecordIds([...linkedRecordIds, id]);
      }
    }
  };

  const removeLinkedRecord = (id: string) => {
    const index = linkedRecordIds.findIndex((_id) => _id === id);
    if (index !== -1) {
      setLinkedRecordIds([...linkedRecordIds.slice(0, index), ...linkedRecordIds.slice(index + 1)]);
    }
  };

  useEffect(() => {
    setSelectedRecords(linkedRecordIds);
  }, [linkedRecordIds, setSelectedRecords]);

  const configId =
    selectedConfig && !isDropDownNoSelection(selectedConfig) ? (selectedConfig.value as number) : undefined;

  const gridWrapperStyles = clsx('relative overflow-hidden', {
    'h-1/2': !isCloneRecordLinkType,
    'h-full': isCloneRecordLinkType,
  });

  return (
    <div className="flex h-full w-full flex-col gap-5">
      <div className={gridWrapperStyles}>
        <AllRecordsGrid
          recordId={recordId}
          formId={formId}
          configId={configId}
          defaultGridSettings={allRecordsDefaultGridSettings}
          columnConfigurator={allRecordsColumnsConfigurator}
          rowIndicator={optionRowIndicator}
          onAdded={addLinkedRecord}
          highlightedRows={linkedRecordIds}
          previewRecordId={previewRecordId}
          selectedLastEditedFilter={selectedLastEditedFilter}
          selectedRecordsFilter={selectedRecordsFilter}
        />
      </div>
      {!isCloneRecordLinkType && (
        <div className={gridWrapperStyles}>
          <LinkedRecordsGrid
            recordIds={linkedRecordIds}
            formId={targetModuleFormId}
            configId={configId}
            defaultGridSettings={selectedRecordsDefaultGridSettings}
            columnConfigurator={selectedRecordsColumnConfigurator}
            rowIndicator={selectedRowIndicator}
            onRemoved={removeLinkedRecord}
          />
        </div>
      )}
    </div>
  );
}
